import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { container } from "tsyringe";
import { useProjectTeamContext } from "components/pages/ProfessionalSpace/contexts/ProjectTeam";
import ProjectTeamCastResponseResource from "common/resources/Project/Config/Team/Cast/ProjectTeamCastResponseResource";
import { upsert } from "common/utils/array";
import { useProjectId } from "hooks/useProjectId";
import CastService from "services/projects/CastService";
import CastingCard from "./CastingCard";
import { ECastingCardModal, useCastingCardModal } from "./CastingCardModal";
import CreateCastingModal from "./CastingCardModal/CreateCastingModal";
import EditCastingModal from "./CastingCardModal/EditCastingModal";
import DeleteCastingModal from "./CastingCardModal/DeleteCastingModal";
import classes from "./classes.module.scss";

type IProps = {};

const castService = container.resolve(CastService);

export const CastingList = forwardRef<{ onOpenCreateModal: () => void }, IProps>((_props, ref) => {
	const { casts, removeCast, upsertCast } = useCasting();
	const { selectedCast, isModalOpen, onCloseModal, onOpenCreateModal, onOpenEditModal, onOpenDeleteModal } = useCastingCardModal();

	useImperativeHandle(ref, () => ({
		onOpenCreateModal,
	}));

	const onCreated = useCallback(
		(cast: ProjectTeamCastResponseResource) => {
			upsertCast(cast);
			onCloseModal();
		},
		[onCloseModal, upsertCast],
	);

	const onEdited = useCallback(
		(cast: ProjectTeamCastResponseResource) => {
			upsertCast(cast);
			onCloseModal();
		},
		[onCloseModal, upsertCast],
	);

	const onDeleted = useCallback(
		(deletedCast: Pick<ProjectTeamCastResponseResource, "id">) => {
			removeCast(deletedCast);
			onCloseModal();
		},
		[onCloseModal, removeCast],
	);

	return (
		<div className={classes["root"]}>
			<div className={classes["casts-container"]}>
				{casts.map((cast) => (
					<CastingCard key={cast.id} cast={cast} onEditClick={() => onOpenEditModal(cast)} onDeleteClick={() => onOpenDeleteModal(cast)} />
				))}
			</div>

			{isModalOpen(ECastingCardModal.createCast) && <CreateCastingModal isOpen onCreated={onCreated} onClose={onCloseModal} />}
			{isModalOpen(ECastingCardModal.editCast) && selectedCast && <EditCastingModal isOpen cast={selectedCast} onEdited={onEdited} onClose={onCloseModal} />}
			{isModalOpen(ECastingCardModal.deleteCast) && selectedCast && <DeleteCastingModal isOpen cast={selectedCast} onDeleted={onDeleted} onClose={onCloseModal} />}
		</div>
	);
});

const useCasting = () => {
	const { projectId } = useProjectId();
	const { projectTeamId } = useProjectTeamContext();

	const [casts, setCasts] = useState<ProjectTeamCastResponseResource[]>([]);

	const fetchCasts = useCallback(async () => {
		return castService
			.getAllByProjectTeamId(projectId, projectTeamId)
			.then(setCasts)
			.catch((error) => {
				console.error(error);
				setCasts([]);
			});
	}, [projectId, projectTeamId]);

	const upsertCast = useCallback((cast: ProjectTeamCastResponseResource) => {
		setCasts((casts) => upsert(casts, cast, (c) => c.id === cast.id));
	}, []);

	const removeCast = useCallback((deletedCast: Pick<ProjectTeamCastResponseResource, "id">) => {
		setCasts((casts) => casts.filter((c) => c.id !== deletedCast.id));
	}, []);

	useEffect(() => {
		fetchCasts();
	}, [fetchCasts]);

	return { casts, removeCast, upsertCast };
};
